<template>
  <div id="multi-bulk-upload" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-card class="mt-4 px-8 py-6">
            <div class="text-center">
              To use this feature you must access your Supplier portal from a
              Desktop device
            </div>
            <div class="text-center">
              <v-btn
                class="i-btn-block-secondary mt-4"
                rounded
                :to="{ name: 'dashboard' }"
                >Dashboard</v-btn
              >
            </div>
          </v-card>
        </template>

        <template v-else>
          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/volume-buys/multi-upload"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div>
                <i-multi-volume-buy-upload-row
                  v-for="(volumeBuy, index) in volumeBuys"
                  :key="index"
                  :is-duplicate="hasDuplicate(index)"
                  :family-groups="listFamilyGroups"
                  :products="productOptionsForRow(index)"
                  :products-loading="rowProductsLoading(index)"
                  :products-data="[]"
                  :volume-buy="volumeBuy"
                  :volumeBuyPosition="index"
                  :volumeBuysQty="volumeBuys.length"
                  :is-only-row="volumeBuys.length === 1"
                  :is-row-valid="isRowValid(volumeBuy, index)"
                  :initial-value="initialRowValue"
                  @change="onFieldChange(index, $event)"
                  @remove="onVolumeBuyRemove(index)"
                  @add="onVolumeBuyAdd(index)"
                  @duplicate="onDuplicate(index)"
                  @clear="onClear(index)"
                  @ensure-products="ensureProductsLoaded(index)"
                  @search-products="onProductSearch(index, $event)"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="upload-volume-buys-col text-right">
              <v-card class="pa-4">
                <v-btn
                  class="i-btn-block-secondary"
                  :disabled="!isFormValid"
                  :loading="saving"
                  @click="onSave"
                >
                  <strong>Upload Volume Buys</strong>
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { createVolumePromotions } from '@/services/volumePromotions'
import VolumePromotionHelper from '@/modules/dataMaintenance/helper/VolumePromotionHelper'
import LocalStorageHelper from '@/components/helpers/LocalStorageHelper'
import iMultiVolumeBuyUploadRow from '../components/MultiVolumeBuyUploadRow'
import iInstructions from '@/components/Instructions'
import moment from 'moment-timezone'
import { PROMOTION_TYPE } from '@/modules/dataMaintenance/constants/Special.constants'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange,
  productHasUpcomingPriceChangeByCode,
  withUpcomingPriceChangeDisabled
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'

const initialPromotion = {
  regionalPriceMap: [],
  isServiceProduct: false,
  selectedProductDisplayName: null
}

const MULTI_VOLUME_BUYS_STORAGE_KEY = 'multiple-volume-buys'
const PRODUCT_SEARCH_RESULT_LIMIT = 25

export default {
  name: 'multi-bulk-upload',

  components: {
    iMultiVolumeBuyUploadRow,
    iInstructions
  },

  data: () => ({
    saving: false,
    volumeBuys: [],
    initialRowValue: initialPromotion,
    productOptionsByRow: {},
    productsLoadingByRow: {},
    hasLoadedProductsByRow: {}
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('familyGroups', ['familyGroups']),
    ...mapState('products', {
      products: 'products',
      productsLoading: 'loading'
    }),
    ...mapState('pricingRegions', ['pricingRegions']),
    ...mapGetters('tenant', ['tenantCountryRegion']),

    localStorageKey() {
      return `${MULTI_VOLUME_BUYS_STORAGE_KEY}-${this.selectedSupplierId}`
    },

    isUsTenant() {
      return this.tenantCountryRegion === 'US'
    },

    isFormValid() {
      return VolumePromotionHelper.validatePromotions(
        this.volumeBuys.map((promotion) => ({
          ...promotion,
          isUsTenant: this.isUsTenant
        }))
      )
    },

    enrichedFamilyGroups() {
      return this.familyGroups
    },

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    listFamilyGroups() {
      // familyGroups defaults to undefined in the store until fetchFamilyGroups
      // (dispatched below in mounted()) resolves, and this computed runs on the
      // very first render, before that fetch even starts.
      if (!this.familyGroups) {
        return []
      }
      return [
        ...this.familyGroups
          // filter out familyGroups without products
          .filter((f) => f.productCodes.length)
          .map((f) => ({ text: f.name, value: f.id }))
          .map((option) =>
            withUpcomingPriceChangeDisabled(
              option,
              familyGroupHasUpcomingPriceChange(
                this.familyGroups.find((group) => group.id === option.value),
                this.upcomingPriceChangeProductCodes
              )
            )
          )
          .sort((p1, p2) => (p1.text < p2.text ? -1 : 1))
      ]
    },

    isProductsLoading() {
      return this.productsLoading
    }
  },

  created() {
    this.productSearchDebouncersByRow = {}
    this.productDetailsByCode = {}
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),

    normalizeProductsResponse(response) {
      if (Array.isArray(response)) return response
      return response?.products || []
    },

    cacheProducts(products = []) {
      products.forEach((product) => {
        if (!product?.productCode) return
        this.productDetailsByCode[product.productCode] = {
          ...product,
          zoneCodesWithPrice: Object.keys(product.regionalPriceMap || {}).map(
            (code) => this.pricingRegions.find((zone) => code === zone.code)
          )
        }
      })
    },

    createProductOption(product) {
      return withUpcomingPriceChangeDisabled(
        {
          text: `${product.supplierItemId} / ${product.tradeItemDescription}`,
          value: product.productCode
        },
        productHasUpcomingPriceChange(
          product,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        )
      )
    },

    productOptionsForRow(index) {
      return this.productOptionsByRow[index] || []
    },

    rowProductsLoading(index) {
      return !!this.productsLoadingByRow[index]
    },

    pinSelectedProductOption(index, productCode, displayName = null) {
      if (!productCode) return

      const product =
        this.productDetailsByCode[productCode] ||
        (displayName
          ? {
              productCode,
              supplierItemId: displayName.split(' / ')[0] || productCode,
              tradeItemDescription: displayName
                .split(' / ')
                .slice(1)
                .join(' / ')
            }
          : null)

      if (!product) return

      const currentOptions = this.productOptionsForRow(index)
      const selectedOption = displayName
        ? { text: displayName, value: productCode }
        : this.createProductOption(product)

      this.productOptionsByRow[index] = [
        selectedOption,
        ...currentOptions.filter((option) => option?.value !== productCode)
      ]
    },

    setRowProductOptions(index, products = [], selectedProductCode = null) {
      const options = products
        .filter((product) => product.status === 'LIVE')
        .map((product) => this.createProductOption(product))
        .sort((left, right) => (left.text < right.text ? -1 : 1))

      const pinnedProductCode =
        selectedProductCode || this.volumeBuys[index]?.selectedProduct
      const pinnedDisplayName =
        this.volumeBuys[index]?.selectedProductDisplayName || null

      if (pinnedProductCode) {
        const selectedOption = options.find(
          (option) => option.value === pinnedProductCode
        ) || {
          text: pinnedDisplayName || pinnedProductCode,
          value: pinnedProductCode
        }

        this.productOptionsByRow[index] = [
          selectedOption,
          ...options.filter((option) => option.value !== pinnedProductCode)
        ]
        return
      }

      this.productOptionsByRow[index] = options
    },

    async fetchProductsForRow(index, query = '') {
      this.productsLoadingByRow[index] = true

      try {
        const response = await this.fetchProducts({
          supplierId: this.selectedSupplierId,
          status: 'LIVE',
          page: 0,
          size: PRODUCT_SEARCH_RESULT_LIMIT,
          query: query || undefined
        })
        const products = this.normalizeProductsResponse(response)
        this.cacheProducts(products)
        this.setRowProductOptions(index, products)
        this.hasLoadedProductsByRow[index] = true
      } finally {
        this.productsLoadingByRow[index] = false
      }
    },

    ensureProductsLoaded(index) {
      if (this.hasLoadedProductsByRow[index]) return Promise.resolve()
      return this.fetchProductsForRow(index)
    },

    onProductSearch(index, query = '') {
      const normalizedQuery = query.trim()

      if (!normalizedQuery) {
        return this.fetchProductsForRow(index)
      }

      if (!this.productSearchDebouncersByRow[index]) {
        this.productSearchDebouncersByRow[index] = _.debounce(
          (nextQuery) => this.fetchProductsForRow(index, nextQuery),
          250
        )
      }

      return this.productSearchDebouncersByRow[index](normalizedQuery)
    },

    isRowValid(promotion, index) {
      return (
        VolumePromotionHelper.isPromotionValid({
          ...promotion,
          isUsTenant: this.isUsTenant
        }) &&
        this.isPromotionProductAvailable(promotion) &&
        !VolumePromotionHelper.isRowDuplicate(this.volumeBuys, promotion, index)
      )
    },

    isPromotionProductAvailable(promotion) {
      const selectedFamilyGroup = this.familyGroups.find(
        (familyGroup) => familyGroup.id === promotion?.selectedFamilyGroup
      )

      return (
        !productHasUpcomingPriceChangeByCode(
          this.upcomingPriceChangeProductCodes,
          promotion?.selectedProduct
        ) &&
        !familyGroupHasUpcomingPriceChange(
          selectedFamilyGroup,
          this.upcomingPriceChangeProductCodes
        )
      )
    },

    hasDuplicate(promotionIndex) {
      return VolumePromotionHelper.isRowDuplicate(
        this.volumeBuys,
        this.volumeBuys[promotionIndex],
        promotionIndex
      )
    },

    async saveVolumeBuys() {
      const volumeBuys = this.volumeBuys
      const processPromotionsPayload = volumeBuys.map((promotion) => ({
        productCode: promotion.selectedProduct,
        familyGroupId: promotion.selectedFamilyGroup,
        startDate: promotion.startDate,
        endDate: promotion.endDate,
        name: promotion.promotionName,
        targetBannerGroupIds: promotion.targetBannerGroupIds,
        targetMultiStoreOperatorIds: promotion.targetMultiStoreOperatorIds,
        targetStoreIds: promotion.targetStoreIds,
        zonePrices: VolumePromotionHelper.mappedForAPI(
          promotion.zonePrices,
          promotion.isServiceProduct
        )
      }))

      return await createVolumePromotions(
        this.selectedSupplierId,
        processPromotionsPayload
      )
    },

    async onSave() {
      this.saving = true
      try {
        await this.saveVolumeBuys()
        this.addNotification({
          color: 'secondary',
          text: 'Volume Buys have been created'
        })
        LocalStorageHelper.removeItem(this.localStorageKey)
        // Prepare to allow user to create new Volume Buys
        this.volumeBuys = [{ ...initialPromotion }]
      } catch (err) {
        let msg = null

        switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
          case 'VOLUME_PROMOTION_OVERLAP':
            msg =
              'This family group or product has an existing Volume Buy for the selected dates!'
            break
          default:
            msg = 'Volume Buy could not be created...'
        }

        this.addNotification({ color: 'error', text: msg })
      } finally {
        this.saving = false
        await this.$router.push({
          name: 'volume-buys',
          query: { status: 'UPCOMING' }
        })
      }
    },

    updateProductRegionalPriceMap(index, selectedProduct) {
      const product = this.productDetailsByCode[selectedProduct]

      const zonePrices =
        VolumePromotionHelper.transformProductZonePricesToRegionalPriceMap(
          product.zoneCodesWithPrice,
          product.regionalPriceMap
        )

      this.setGPPInfo(index, product)
      this.volumeBuys[index].zonePrices = zonePrices
      return product
    },

    enrichWithOriginalProductRrp(regionalPriceMap, referenceProduct) {
      return regionalPriceMap.map((zone) => ({
        ...zone,
        originalUnitRrp:
          zone.originalUnitRrp ||
          referenceProduct?.regionalPriceMap?.[zone.code]?.[0]?.regionUnitRrp ||
          referenceProduct?.regionalPriceMap?.[zone.code]?.[0]?.rrp ||
          null
      }))
    },

    getReferenceFamilyGroupProduct(productCodes = []) {
      const familyProducts = Object.values(
        this.productDetailsByCode || {}
      ).filter((p) => productCodes.includes(p.productCode))
      if (!familyProducts.length) return null

      const lowestPackageQuantity = Math.min(
        ...familyProducts.map((product) => Number(product.packageQuantity || 0))
      )
      return (
        familyProducts.find(
          (product) =>
            Number(product.packageQuantity || 0) === lowestPackageQuantity
        ) || null
      )
    },

    updateFamilyGroupRegionalPriceMap(index, selectedFamilyGroup) {
      const familyGroup = this.enrichedFamilyGroups?.find(
        ({ id }) => id === selectedFamilyGroup
      )

      if (!familyGroup) return null

      const regionalPriceMap =
        VolumePromotionHelper.transformFamilyGroupZonePricesToRegionalPriceMap(
          familyGroup.zonePrices,
          this.pricingRegions
        )
      const referenceProduct = this.getReferenceFamilyGroupProduct(
        familyGroup.productCodes
      )
      const zonePrices = this.enrichWithOriginalProductRrp(
        regionalPriceMap,
        referenceProduct
      )

      this.setGPPInfo(index, familyGroup)
      this.volumeBuys[index].zonePrices = zonePrices
      return familyGroup
    },

    setGPPInfo(index, selection) {
      const { hasGst, hasWet, packageQuantity, variableWeightTargetInKg } =
        selection
      this.volumeBuys[index].hasGst = hasGst
      this.volumeBuys[index].hasWet = hasWet
      this.volumeBuys[index].packageQuantity = packageQuantity
      this.volumeBuys[index].variableWeightTargetInKg = variableWeightTargetInKg
    },

    async onFieldChange(index, fields) {
      if (fields.promotionTypes === PROMOTION_TYPE.BY_PRODUCT) {
        await this.ensureProductsLoaded(index)
      }

      if (fields.selectedFamilyGroup) {
        const familyGroup = this.updateFamilyGroupRegionalPriceMap(
          index,
          fields.selectedFamilyGroup
        )
        if (familyGroup) {
          fields.promotionName = `${familyGroup.name}`
          fields.isServiceProduct = false
        }
      } else if (fields.selectedProduct) {
        const selectedProduct =
          this.productDetailsByCode[fields.selectedProduct]
        const product = this.updateProductRegionalPriceMap(
          index,
          fields.selectedProduct
        )
        fields.promotionName = `${product.tradeItemDescription}`
        fields.isServiceProduct = product.productType === 'SERVICE'
        fields.selectedProductDisplayName = selectedProduct
          ? `${selectedProduct.supplierItemId} / ${selectedProduct.tradeItemDescription}`
          : fields.selectedProduct
        this.pinSelectedProductOption(
          index,
          fields.selectedProduct,
          fields.selectedProductDisplayName
        )
      }
      for (const [key, value] of Object.entries(fields)) {
        this.volumeBuys[index][key] = value
      }
      this.triggerChanged()
    },

    onVolumeBuyRemove(index) {
      this.volumeBuys.splice(index, 1)
      this.triggerChanged()
    },

    onVolumeBuyAdd(index) {
      this.volumeBuys.splice(index + 1, 0, { ...initialPromotion })
      this.triggerChanged()
    },

    onDuplicate(index) {
      const duplicateIndex = index + 1
      const duplicate = _.cloneDeep(this.volumeBuys[index])
      this.copyProductRowState(index, duplicateIndex)
      this.volumeBuys.splice(duplicateIndex, 0, duplicate)
      this.pinSelectedProductOption(
        duplicateIndex,
        duplicate.selectedProduct,
        duplicate.selectedProductDisplayName
      )
      this.triggerChanged()
    },

    copyProductRowState(sourceIndex, duplicateIndex) {
      ;[
        this.productOptionsByRow,
        this.productsLoadingByRow,
        this.hasLoadedProductsByRow
      ].forEach((rowState) => {
        Object.keys(rowState)
          .map(Number)
          .filter((rowIndex) => rowIndex >= duplicateIndex)
          .sort((left, right) => right - left)
          .forEach((rowIndex) => {
            rowState[rowIndex + 1] = _.cloneDeep(rowState[rowIndex])
          })
      })

      this.productOptionsByRow[duplicateIndex] = []
      this.productsLoadingByRow[duplicateIndex] = false
      this.hasLoadedProductsByRow[duplicateIndex] = false
    },

    triggerChanged() {
      LocalStorageHelper.setItem(this.localStorageKey, this.volumeBuys)
    },

    onClear(index) {
      this.volumeBuys[index] = { ...initialPromotion }
    }
  },

  async mounted() {
    const futureLocalStorageVolumeBuys =
      LocalStorageHelper.getItem(this.localStorageKey)?.filter((p) =>
        moment(p.startDate).isAfter(moment().endOf('day'))
      ) || []
    LocalStorageHelper.setItem(
      this.localStorageKey,
      futureLocalStorageVolumeBuys
    )
    this.volumeBuys = futureLocalStorageVolumeBuys.length
      ? futureLocalStorageVolumeBuys
      : [{ ...initialPromotion }]

    this.volumeBuys.forEach((promotion, index) => {
      if (promotion?.selectedProduct) {
        this.pinSelectedProductOption(
          index,
          promotion.selectedProduct,
          promotion.selectedProductDisplayName
        )
      }
    })

    try {
      await this.fetchFamilyGroups(this.selectedSupplierId)
      this.volumeBuys = this.volumeBuys.map((promotion) => {
        const selectedProduct = this.products.find(
          (p) => p.productCode === promotion.selectedProduct
        )
        return {
          ...promotion,
          isServiceProduct:
            typeof promotion.isServiceProduct === 'boolean'
              ? promotion.isServiceProduct
              : selectedProduct?.productType === 'SERVICE'
        }
      })
    } catch (e) {
      this.addNotification({
        color: 'error',
        text: 'Volume Buys data could not be loaded, please try again...'
      })
      if (!this.volumeBuys.length) {
        this.volumeBuys = [{ ...initialPromotion }]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#multi-bulk-upload {
  .upload-volume-buys-col {
    position: sticky;
    bottom: 0;
    margin: 0;
    padding-bottom: 0;
    width: 100%;
    .upload-promo-btn {
      width: 220px;
    }
  }
}
</style>
